import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Save, RefreshCw, Mail, ChevronDown, ChevronUp, Eye, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { sanitizeEmailHtml } from '../../utils/sanitize';

const EmailTemplatesManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editMode, setEditMode] = useState({});
  const [editedData, setEditedData] = useState({});
  const [previewMode, setPreviewMode] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/email-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch email templates',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch email templates',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (templateKey) => {
    const template = templates.find(t => t.template_key === templateKey);
    setEditMode({ ...editMode, [templateKey]: true });
    setEditedData({
      ...editedData,
      [templateKey]: {
        subject: template.subject,
        html_body: template.html_body,
        text_body: template.text_body
      }
    });
  };

  const handleCancel = (templateKey) => {
    setEditMode({ ...editMode, [templateKey]: false });
    setEditedData({ ...editedData, [templateKey]: null });
  };

  const handleSave = async (templateKey) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/email-templates/${templateKey}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedData[templateKey])
      });

      if (response.ok) {
        const updatedTemplate = await response.json();
        setTemplates(templates.map(t => 
          t.template_key === templateKey ? updatedTemplate : t
        ));
        setEditMode({ ...editMode, [templateKey]: false });
        toast({
          title: 'Success',
          description: 'Email template updated successfully'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update template',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Error',
        description: 'Failed to update template',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/email-templates/${templateToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Email template deleted successfully'
        });
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
        fetchTemplates(); // Refresh list
      } else {
        toast({
          title: 'Error',
          description: 'Failed to delete template',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };


  const togglePreview = (templateKey) => {
    setPreviewMode({ ...previewMode, [templateKey]: !previewMode[templateKey] });
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'user': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'trainer': return 'bg-red-100 text-red-800 dark:bg-purple-900 dark:text-purple-200';
      case 'booking': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold dark:text-gray-100">Email Templates</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage all automated email content sent to users and trainers
          </p>
        </div>
        <Button onClick={fetchTemplates} variant="outline" className="w-full sm:w-auto">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4">
          {templates.map((template) => {
            const isEditing = editMode[template.template_key];
            const isPreviewing = previewMode[template.template_key];
            const isExpanded = expandedTemplate === template.template_key;
            
            return (
              <Card key={template.id} className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader className="cursor-pointer" onClick={() => setExpandedTemplate(isExpanded ? null : template.template_key)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <CardTitle className="dark:text-gray-100 text-base sm:text-lg break-words">{template.name}</CardTitle>
                      </div>
                      <Badge className={`${getCategoryColor(template.category)} text-xs flex-shrink-0`}>
                        {template.category}
                      </Badge>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
                      {template.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="space-y-4 px-3 sm:px-6">
                  {/* Variables */}
                  <div className="overflow-hidden">
                    <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                      Available Variables
                    </label>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2">
                      {template.variables.map((variable) => (
                        <Badge key={variable} variant="outline" className="font-mono text-[10px] sm:text-xs break-all">
                          {`{${variable}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="overflow-hidden">
                    <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                      Subject Line
                    </label>
                    {isEditing ? (
                      <Input
                        value={editedData[template.template_key]?.subject || ''}
                        onChange={(e) => setEditedData({
                          ...editedData,
                          [template.template_key]: {
                            ...editedData[template.template_key],
                            subject: e.target.value
                          }
                        })}
                        className="mt-1 dark:bg-gray-700 dark:border-gray-600 text-sm"
                      />
                    ) : (
                      <p className="mt-1 p-2 bg-gray-50 dark:bg-gray-700 rounded border dark:border-gray-600 text-xs sm:text-sm break-words">
                        {template.subject}
                      </p>
                    )}
                  </div>

                  {/* Text Body - Fallback */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Plain Text Body (Fallback for old email clients)
                      </label>
                    </div>
                    {isEditing ? (
                      <Textarea
                        value={editedData[template.template_key]?.text_body || ''}
                        onChange={(e) => setEditedData({
                          ...editedData,
                          [template.template_key]: {
                            ...editedData[template.template_key],
                            text_body: e.target.value
                          }
                        })}
                        rows={6}
                        className="mt-1 text-sm dark:bg-gray-700 dark:border-gray-600"
                      />
                    ) : (
                      <pre className="mt-1 p-2 bg-gray-50 dark:bg-gray-700 rounded border dark:border-gray-600 text-[10px] sm:text-xs overflow-x-auto overflow-y-auto max-h-32 whitespace-pre-wrap break-words">
                        {template.text_body}
                      </pre>
                    )}
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                      ℹ️ Only used by very old email clients that don't support HTML. Most users see the HTML version above.
                    </p>
                  </div>

                  {/* HTML Body - NOW EDITABLE */}
                  <div className="overflow-hidden">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                        HTML Body (Email Design)
                      </label>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        {isEditing && (
                          <span className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded text-center">
                            💡 Preview before saving
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => togglePreview(template.template_key)}
                          className="w-full sm:w-auto"
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {isPreviewing ? 'Hide' : 'Show'} Preview
                        </Button>
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="mt-1">
                        <Textarea
                          value={editedData[template.template_key]?.html_body || ''}
                          onChange={(e) => setEditedData({
                            ...editedData,
                            [template.template_key]: {
                              ...editedData[template.template_key],
                              html_body: e.target.value
                            }
                          })}
                          rows={15}
                          className="mt-1 text-xs font-mono dark:bg-gray-700 dark:border-gray-600"
                          placeholder="Enter HTML code here..."
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          💡 Tip: Use HTML tags like &lt;h1&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;a&gt;, etc. Variables: {template.variables.map(v => `{${v}}`).join(', ')}
                        </p>
                      </div>
                    ) : (
                      <pre className="mt-1 p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded border dark:border-gray-600 text-[10px] sm:text-xs overflow-x-auto overflow-y-auto max-h-64 font-mono whitespace-pre-wrap break-all">
                        {template.html_body}
                      </pre>
                    )}
                    
                    {/* Live Preview */}
                    {isPreviewing && (
                      <div className="mt-4 space-y-4 overflow-hidden">
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                            Live HTML Email Preview
                          </p>
                          <div className="p-3 sm:p-4 bg-white dark:bg-gray-800 dark:bg-gray-900 rounded border-2 border-blue-300 dark:border-blue-600 overflow-x-auto overflow-y-auto max-h-96">
                            <div className="max-w-full" dangerouslySetInnerHTML={{ 
                              __html: sanitizeEmailHtml(isEditing 
                                ? editedData[template.template_key]?.html_body || template.html_body
                                : template.html_body)
                            }} />
                          </div>
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-2">
                            ℹ️ This is how the email will look in most email clients (Gmail, Outlook, etc.)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t dark:border-gray-700">
                    {isEditing ? (
                      <>
                        <Button
                          onClick={() => handleSave(template.template_key)}
                          disabled={saving}
                          className="flex-1 w-full sm:w-auto"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button
                          onClick={() => handleCancel(template.template_key)}
                          variant="outline"
                          disabled={saving}
                          className="w-full sm:w-auto"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={() => handleEdit(template.template_key)} className="w-full sm:w-auto">
                          Edit Template
                        </Button>
                        <Button 
                          onClick={() => {
                            setTemplateToDelete(template.template_key);
                            setDeleteDialogOpen(true);
                          }}
                          variant="outline"
                          className="w-full sm:w-auto border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/30"
                          disabled={saving}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete Template
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Last Updated */}
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Last updated: {new Date(template.updated_at).toLocaleString()}
                  </p>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the <strong>"{templateToDelete}"</strong> template? 
              This action cannot be undone and will permanently remove this email template from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTemplateToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Deleting...' : 'Yes, Delete Template'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default EmailTemplatesManager;
